import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  BadgeCheck,
  BookOpen,
  Building2,
  Calculator,
  Calendar,
  Coins,
  Database,
  FileCheck2,
  FilePlus2,
  FileText,
  Gauge,
  Globe,
  Hash,
  Landmark,
  LineChart,
  Lock,
  Network,
  Package,
  PiggyBank,
  Play,
  Radio,
  Receipt,
  Repeat,
  ScrollText,
  Settings2,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Tags,
  Timer,
  TrendingUp,
  UserCog,
  Wallet,
  Workflow,
} from 'lucide-react';

export type NavItem = {
  /** Surface key used by the registry. */
  surface: string;
  /** i18n key under `nav:surfaces.*`. */
  labelKey: string;
  /** Scopes required (ANY one hides the item if missing). */
  scopes: string[];
  icon: LucideIcon;
};

export type NavSection = {
  /** i18n key under `nav:sections.*`. */
  labelKey: string;
  items: NavItem[];
  /**
   * Optional icon rendered next to the section header label.
   * Useful for privileged / thematic sections (e.g. Access Administration).
   */
  icon?: LucideIcon;
};

/**
 * Full surface catalog from the plan's Appendix A.
 * Each item shows up in the LeftNav only if the user has all listed scopes.
 * Unregistered surfaces still appear — they render FallbackSurface when opened.
 */
export const navConfig: NavSection[] = [
  {
    labelKey: 'trades',
    items: [
      { surface: 'bond_deal_workbook', labelKey: 'bond_deal_workbook', scopes: ['deals:capture', 'deals:read'], icon: BookOpen },
      { surface: 'bond_deal_amend', labelKey: 'bond_deal_amend', scopes: ['deals:amend'], icon: FilePlus2 },
      { surface: 'bond_pretrade_ticket', labelKey: 'bond_pretrade_ticket', scopes: ['pretrade:read'], icon: BadgeCheck },
      { surface: 'batch_deal_capture', labelKey: 'batch_deal_capture', scopes: ['deals:capture'], icon: FilePlus2 },
      { surface: 'deal_list', labelKey: 'deal_list', scopes: ['deals:read'], icon: FileText },
      { surface: 'deal_detail', labelKey: 'deal_detail', scopes: ['deals:read'], icon: FileCheck2 },
      { surface: 'deal_history', labelKey: 'deal_history', scopes: ['deals:read'], icon: ScrollText },
      { surface: 'fx_spot', labelKey: 'fx_spot', scopes: ['deals:capture'], icon: Coins },
      { surface: 'fx_forward', labelKey: 'fx_forward', scopes: ['deals:capture'], icon: Coins },
      { surface: 'fx_ndf', labelKey: 'fx_ndf', scopes: ['deals:capture'], icon: Coins },
      { surface: 'fx_option_vanilla', labelKey: 'fx_option_vanilla', scopes: ['deals:capture'], icon: Coins },
      { surface: 'fx_option_barrier', labelKey: 'fx_option_barrier', scopes: ['deals:capture'], icon: Coins },
      { surface: 'irs_vanilla', labelKey: 'irs_vanilla', scopes: ['deals:capture'], icon: Repeat },
      { surface: 'irs_ois', labelKey: 'irs_ois', scopes: ['deals:capture'], icon: Repeat },
      { surface: 'irs_basis', labelKey: 'irs_basis', scopes: ['deals:capture'], icon: Repeat },
      { surface: 'irs_xccy', labelKey: 'irs_xccy', scopes: ['deals:capture'], icon: Repeat },
      { surface: 'fra', labelKey: 'fra', scopes: ['deals:capture'], icon: Timer },
      { surface: 'swaption', labelKey: 'swaption', scopes: ['deals:capture'], icon: Sparkles },
      { surface: 'repo', labelKey: 'repo', scopes: ['deals:capture'], icon: Landmark },
      { surface: 'deposit', labelKey: 'deposit', scopes: ['deals:capture'], icon: PiggyBank },
      { surface: 'cds', labelKey: 'cds', scopes: ['deals:capture'], icon: ShieldCheck },
      { surface: 'equity_option', labelKey: 'equity_option', scopes: ['deals:capture'], icon: LineChart },
      { surface: 'custom_product', labelKey: 'custom_product', scopes: ['deals:capture'], icon: Package },
      { surface: 'pre_trade_check', labelKey: 'pre_trade_check', scopes: ['deals:pre-check'], icon: BadgeCheck },
      { surface: 'portfolio_positions', labelKey: 'portfolio_positions', scopes: ['positions:read'], icon: TrendingUp },
      { surface: 'portfolio_summary', labelKey: 'portfolio_summary', scopes: ['positions:read'], icon: Gauge },
      { surface: 'approval_context', labelKey: 'approval_context', scopes: ['approvals:read'], icon: ScrollText },
    ],
  },
  {
    labelKey: 'instruments',
    items: [
      { surface: 'instrument_manager', labelKey: 'instrument_manager', scopes: ['instruments:create'], icon: FilePlus2 },
      { surface: 'instrument_list', labelKey: 'instrument_list', scopes: ['instruments:read'], icon: BookOpen },
      { surface: 'instrument_detail', labelKey: 'instrument_detail', scopes: ['instruments:read'], icon: FileCheck2 },
      { surface: 'instrument_events', labelKey: 'instrument_events', scopes: ['instruments:read'], icon: Activity },
    ],
  },
  {
    labelKey: 'marketData',
    items: [
      { surface: 'curve_viewer', labelKey: 'curve_viewer', scopes: ['indexes:read'], icon: LineChart },
      { surface: 'capture_fixing', labelKey: 'capture_fixing', scopes: ['fixings:capture', 'indexes:read'], icon: Hash },
      { surface: 'fixings_query', labelKey: 'fixings_query', scopes: ['fixings:read'], icon: Database },
      { surface: 'market_index_manager', labelKey: 'market_index_manager', scopes: ['indexes:read'], icon: Tags },
    ],
  },
  {
    labelKey: 'riskValuation',
    items: [
      { surface: 'run_valuation', labelKey: 'run_valuation', scopes: ['valuations:write'], icon: Play },
      { surface: 'valuation_detail', labelKey: 'valuation_detail', scopes: ['valuations:read'], icon: Calculator },
      { surface: 'create_simulation', labelKey: 'create_simulation', scopes: ['simulations:write'], icon: FilePlus2 },
      { surface: 'run_simulation', labelKey: 'run_simulation', scopes: ['simulations:write'], icon: Play },
      { surface: 'simulation_results', labelKey: 'simulation_results', scopes: ['simulations:read'], icon: Gauge },
      { surface: 'credit_lines', labelKey: 'credit_lines', scopes: ['credit:read'], icon: ShieldCheck },
      { surface: 'credit_exposure', labelKey: 'credit_exposure', scopes: ['credit:read'], icon: Activity },
      { surface: 'fix_cashflow', labelKey: 'fix_cashflow', scopes: ['cashflows:fix'], icon: Repeat },
      { surface: 'portfolio_risk', labelKey: 'portfolio_risk', scopes: ['risk:read'], icon: ShieldAlert },
      { surface: 'rates_desk', labelKey: 'rates_desk', scopes: ['risk:read'], icon: Radio },
    ],
  },
  {
    labelKey: 'postTradeOps',
    items: [
      { surface: 'settlement_list', labelKey: 'settlement_list', scopes: ['settlements:read'], icon: FileText },
      { surface: 'instruct_settlement', labelKey: 'instruct_settlement', scopes: ['settlements:instruct'], icon: Workflow },
      { surface: 'generate_payment', labelKey: 'generate_payment', scopes: ['payments:generate'], icon: Receipt },
      { surface: 'payment_detail', labelKey: 'payment_detail', scopes: ['payments:read'], icon: FileCheck2 },
      { surface: 'execute_netting', labelKey: 'execute_netting', scopes: ['netting:execute', 'netting:read', 'settlements:read'], icon: Network },
      { surface: 'netting_sets', labelKey: 'netting_sets', scopes: ['netting:read'], icon: Network },
      { surface: 'eod_operator', labelKey: 'eod_operator', scopes: ['system:batch'], icon: Play },
      { surface: 'journal_list', labelKey: 'journal_list', scopes: ['journals:read'], icon: BookOpen },
      { surface: 'journal_detail', labelKey: 'journal_detail', scopes: ['journals:read'], icon: FileCheck2 },
      { surface: 'post_settlement_entry', labelKey: 'post_settlement_entry', scopes: ['journals:post'], icon: FilePlus2 },
      { surface: 'period_close', labelKey: 'period_close', scopes: ['journals:period_close'], icon: Lock },
      { surface: 'posting_rules', labelKey: 'posting_rules', scopes: ['journals:read'], icon: SlidersHorizontal },
      { surface: 'cash_balances', labelKey: 'cash_balances', scopes: ['cash:read'], icon: Wallet },
      { surface: 'closeout_batches', labelKey: 'closeout_batches', scopes: ['closeout:read'], icon: Lock },
      { surface: 'matched_book', labelKey: 'matched_book', scopes: ['reports:read'], icon: BookOpen },
      { surface: 'create_instruction', labelKey: 'create_instruction', scopes: ['instructions:create'], icon: FilePlus2 },
      { surface: 'instruction_detail', labelKey: 'instruction_detail', scopes: ['instructions:read'], icon: FileCheck2 },
    ],
  },
  {
    labelKey: 'adminConfig',
    items: [
      { surface: 'audit_event_history', labelKey: 'audit_event_history', scopes: ['audit:read'], icon: ScrollText },
      { surface: 'verify_hash_chain', labelKey: 'verify_hash_chain', scopes: ['audit:read'], icon: Hash },
      { surface: 'valuation_lineage', labelKey: 'valuation_lineage', scopes: ['audit:read'], icon: Workflow },
      { surface: 'schema_catalog', labelKey: 'schema_catalog', scopes: ['config:read'], icon: Database },
      { surface: 'roles_viewer', labelKey: 'roles_viewer', scopes: ['config:read'], icon: ShieldCheck },
      { surface: 'stp_rules_manager', labelKey: 'stp_rules_manager', scopes: ['stp:read'], icon: Workflow },
      { surface: 'approval_chains_viewer', labelKey: 'approval_chains_viewer', scopes: ['config:read'], icon: Workflow },
      { surface: 'asset_class_extensions', labelKey: 'asset_class_extensions', scopes: ['config:read'], icon: Building2 },
      { surface: 'party_hierarchy', labelKey: 'party_hierarchy', scopes: ['parties:internal:read'], icon: Building2 },
      { surface: 'bank_account_manager', labelKey: 'bank_account_manager', scopes: ['bank_accounts:read'], icon: Landmark },
      { surface: 'calendar_manager', labelKey: 'calendar_manager', scopes: ['calendars:manage'], icon: Calendar },
      { surface: 'system_status', labelKey: 'system_status', scopes: ['system:read'], icon: Gauge },
      { surface: 'preferences', labelKey: 'preferences', scopes: [], icon: Settings2 },
    ],
  },
  {
    labelKey: 'plugins',
    items: [
      { surface: 'plugin_artifacts', labelKey: 'plugin_artifacts', scopes: ['artifacts:read'], icon: Package },
      { surface: 'jar_key_registry', labelKey: 'jar_key_registry', scopes: ['plugins:admin'], icon: Globe },
    ],
  },
  /**
   * Access Administration — visible to users with users:admin OR entitlements:admin.
   * The section hides automatically when none of its items pass the per-item
   * scope filter in LeftNav (visibleSections logic), which achieves the OR
   * behaviour: a users:admin-only user sees only the User Access Workbook; an
   * entitlements:admin-only user sees only the Access Policy Console; a user
   * with both scopes sees both; a no-admin user sees nothing and the section
   * collapses entirely.
   */
  {
    labelKey: 'accessAdmin',
    items: [
      { surface: 'user_access_workbook', labelKey: 'user_access_workbook', scopes: ['users:admin'], icon: UserCog },
      { surface: 'access_policy_console', labelKey: 'access_policy_console', scopes: ['entitlements:admin'], icon: ShieldCheck },
    ],
  },
];
